import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, ShieldAlert, Smartphone, Monitor, KeyRound, ArrowLeft, X,
  Check, Copy, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("de-CH"); } catch { return s; }
};

export default function SecurityPage() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // TOTP setup
  const [setupData, setSetupData] = useState(null); // {secret, uri, qr_data_url}
  const [setupCode, setSetupCode] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // disable
  const [disableCode, setDisableCode] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get("/auth/sessions");
      setSessions(res.data.sessions || []);
    } catch { /* ignore */ }
    finally { setLoadingSessions(false); }
  };

  useEffect(() => { loadSessions(); }, []);

  const startSetup = async () => {
    setSetupBusy(true);
    try {
      const res = await api.post("/auth/totp/setup");
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setSetupBusy(false); }
  };

  const activate = async () => {
    setSetupBusy(true);
    try {
      const res = await api.post("/auth/totp/activate", { code: setupCode.trim() });
      setRecoveryCodes(res.data.recovery_codes || []);
      setSetupData(null);
      setSetupCode("");
      toast.success("2FA aktiviert");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setSetupBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("2FA wirklich deaktivieren?")) return;
    setDisableBusy(true);
    try {
      await api.post("/auth/totp/disable", { code: disableCode.trim() });
      toast.success("2FA deaktiviert");
      setDisableCode("");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setDisableBusy(false); }
  };

  const revokeSession = async (jti, isCurrent) => {
    try {
      await api.delete(`/auth/sessions/${jti}`);
      if (isCurrent) {
        await logout();
        navigate("/login");
        return;
      }
      toast.success("Sitzung beendet");
      loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const copyAll = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Alle Codes kopiert");
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12" data-testid="security-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>
        <div>
          <div className="label-uppercase text-prime flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Sicherheit
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Mein Konto schützen</h1>
        </div>
      </div>

      {/* 2FA SECTION */}
      <div className="prime-card p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-prime" /> Zwei-Faktor-Authentifizierung (2FA)
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Schützen Sie Ihr Konto mit einer Authenticator-App (Google Authenticator, Authy, 1Password, …)
            </p>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${user?.totp_enabled ? "bg-green-500/15 text-green-700 dark:text-green-400" : "bg-secondary text-muted-foreground"}`} data-testid="totp-status">
            {user?.totp_enabled ? "AKTIV" : "INAKTIV"}
          </span>
        </div>

        {!user?.totp_enabled && !setupData && !recoveryCodes && (
          <Button onClick={startSetup} disabled={setupBusy} className="btn-prime gap-2" data-testid="totp-start">
            {setupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            2FA einrichten
          </Button>
        )}

        {setupData && (
          <div className="space-y-4">
            <ol className="text-sm space-y-1 text-muted-foreground list-decimal list-inside">
              <li>Öffnen Sie Ihre Authenticator-App.</li>
              <li>Scannen Sie diesen QR-Code <em>oder</em> geben Sie das Secret manuell ein.</li>
              <li>Geben Sie den 6-stelligen Code zur Bestätigung ein.</li>
            </ol>
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <img src={setupData.qr_data_url} alt="QR" className="w-48 h-48 rounded-md bg-white p-2" data-testid="totp-qr" />
              <div className="flex-1 w-full">
                <Label className="label-uppercase text-muted-foreground">Secret (manuell)</Label>
                <div className="mt-1 flex gap-1">
                  <Input value={setupData.secret} readOnly className="font-mono text-xs" data-testid="totp-secret" />
                  <Button type="button" size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success("Kopiert"); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <Label className="label-uppercase text-muted-foreground mt-3 block">Code aus der App</Label>
                <Input
                  inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={setupCode} onChange={e => setSetupCode(e.target.value)}
                  placeholder="123456" autoFocus
                  className="mt-1 h-11 text-center text-lg tracking-[0.4em] font-mono" data-testid="totp-code-activate"
                />
                <div className="flex gap-2 mt-3">
                  <Button onClick={activate} disabled={setupBusy || setupCode.length < 6} className="btn-prime flex-1 gap-2" data-testid="totp-activate-btn">
                    {setupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Aktivieren
                  </Button>
                  <Button variant="outline" onClick={() => { setSetupData(null); setSetupCode(""); }}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {recoveryCodes && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-bold">Recovery-Codes — JETZT speichern!</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Wenn Sie Ihre Authenticator-App verlieren, können Sie sich mit einem dieser einmaligen Codes anmelden.
              Bewahren Sie sie sicher auf (Passwortmanager, ausgedruckt). Diese Codes werden Ihnen <strong>nie wieder</strong> gezeigt.
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {recoveryCodes.map((c, i) => (
                <div key={i} className="bg-secondary px-3 py-2 rounded text-center select-all">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyAll} className="gap-1.5" data-testid="copy-recovery">
                <Copy className="w-3 h-3" /> Alle kopieren
              </Button>
              <Button onClick={() => setRecoveryCodes(null)} className="btn-prime" data-testid="ack-recovery">
                Ich habe sie gespeichert
              </Button>
            </div>
          </div>
        )}

        {user?.totp_enabled && !setupData && !recoveryCodes && (
          <div className="space-y-2">
            <Label className="label-uppercase text-muted-foreground">2FA deaktivieren — Code eingeben</Label>
            <div className="flex gap-2">
              <Input
                inputMode="numeric" pattern="[0-9]*" maxLength={9}
                value={disableCode} onChange={e => setDisableCode(e.target.value)}
                placeholder="6-stelliger Code oder Recovery-Code"
                className="h-10 font-mono text-center" data-testid="totp-disable-code"
              />
              <Button variant="outline" onClick={disable} disabled={disableBusy || !disableCode} className="gap-1" data-testid="totp-disable-btn">
                {disableBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                Deaktivieren
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* SESSIONS SECTION */}
      <div className="prime-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display font-bold text-lg">Aktive Sitzungen</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Maximal 1 Desktop + 1 Mobile gleichzeitig
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadSessions} disabled={loadingSessions}>
            {loadingSessions ? <Loader2 className="w-3 h-3 animate-spin" /> : "Aktualisieren"}
          </Button>
        </div>
        <div className="space-y-2" data-testid="sessions-list">
          {sessions.length === 0 && (
            <div className="text-sm text-muted-foreground italic">Keine aktiven Sitzungen.</div>
          )}
          {sessions.map((s) => {
            const Icon = s.device_type === "mobile" ? Smartphone : Monitor;
            return (
              <div key={s.jti} className="border border-border rounded-md p-3 flex items-center gap-3" data-testid={`row-session-${s.jti}`}>
                <Icon className="w-5 h-5 text-prime shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider">
                      {s.device_type === "mobile" ? "Mobile" : "Desktop"}
                    </span>
                    {s.current && <span className="text-[9px] bg-green-500/15 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">DIESE</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">{s.user_agent}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {fmtDate(s.last_seen_at)} · {s.ip || "—"}
                  </div>
                </div>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => revokeSession(s.jti, s.current)}
                  className="gap-1"
                  data-testid={`revoke-${s.jti}`}
                >
                  <X className="w-3 h-3" /> Beenden
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
